import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators, compose } from 'redux';
import { createStructuredSelector } from 'reselect';
import { Helmet } from 'react-helmet';
import * as watchers from 'ggApp/watchers';

// Actions import
import { getAddressByUser as getAddressByUserAction } from 'ggApp/modules/address/actions';
import {
    getPaymentMethodsByUser as getPaymentMethodsByUserAction,
    getPaymentMethodsByStore as getPaymentMethodsByStoreAction,
} from 'ggApp/modules/payment/actions';
import { authorize as authorizeAction } from 'ggApp/modules/braintree/actions';
import { getOrder as getOrderAction } from 'ggApp/modules/order/actions';
import { statusBarCheckoutTrigger as statusBarCheckoutTriggerAction } from 'ggApp/modules/navigator/actions';
import { checkStoreAndUserType as checkStoreAndUserTypeAction } from 'ggApp/modules/auth/actions';
import { redirectus as redirectAction } from 'ggApp/modules/router/actions';

import BusinessRedirectionModal from 'ggApp/shared/modals/BusinessRedirection';

// Selectors import
import { storeActive as activeStoreSelector } from 'ggApp/modules/store/selectors';
import {
    orderActive as orderSelector,
    fetching as orderFetchingSelector,
    shouldShowPreventCheckoutModalOnError as shouldShowPreventCheckoutModalSelector,
} from 'ggApp/modules/order/selectors';
import { translate, keys as translationsSelector } from 'ggApp/modules/translator/selectors';
import {
    isIncompleteBusinessAccount as isIncompleteBusinessAccountSelector,
    isLegacyBusinessAccount as isLegacyBusinessAccountSelector,
} from 'ggApp/modules/auth/selectors';
import {
    LayoutComponent_Checkout_Header as LayoutComponentCheckoutHeader,
    LayoutComponent_Checkout_Footer as LayoutComponentCheckoutFooter,
    LayoutComponent_Appear as LayoutComponentAppear,
} from 'ggApp/modules/layout';
import NavigatorComponentCheckoutStatusBar from 'ggApp/modules/navigator/components/_checkout/statusBar';

import withNethoneInitialization from 'ggApp/modules/checkout/hooks/withNethoneInitialization';

import './index.scss';
import { blockManagedAccountCheckoutEnabled } from 'ggApp/modules/featureFlag';
import PreventSelfCheckoutModal from 'ggApp/shared/modals/preventSelfCheckout';

const constructRedirectLink = (store) => {
    if (store.store_type === 'grover_pay') {
        return `/${store.country_code.toLowerCase()}-${store.default_language}`;
    }
    return `/${store.code}-${store.default_language}`;
};

class CheckoutWrap extends Component {
    componentWillMount() {
        const { store } = this.context;
        const { order, orderFetching, authorize, getOrder } = this.props;

        if (!order.id && !orderFetching) {
            getOrder();
        } else {
            authorize(order.number);
        }

        this.un = watchers.checkoutOrder(store);
    }

    componentDidMount() {
        const {
            getAddressByUser,
            getPaymentMethodsByUser,
            getPaymentMethodsByStore,
            checkStoreAndUserType,
        } = this.props;

        getAddressByUser();
        getPaymentMethodsByUser();
        getPaymentMethodsByStore();
        checkStoreAndUserType();
    }

    componentWillUnmount() {
        this.un();
    }

    redirectToStore = () => {
        const { redirect, storeActive } = this.props;
        const redirectLink = constructRedirectLink(storeActive);
        redirect(`${redirectLink}`, true);
    };

    render() {
        const {
            children,
            order,
            translations,
            storeActive,
            statusBarCheckoutTrigger,
            checkoutAttemptReference,
            isIncompleteBusinessAccount,
            isLegacyBusinessAccount,
            isBlockManagedAccountCheckout,
            shouldShowPreventCheckoutModalOnError,
        } = this.props;

        const title = translate(translations, storeActive.activeLanguage, 'CHECKOUT_META_TITLE');

        // prevent business users with incomplete account from accessing legacy checkout directly
        if (isIncompleteBusinessAccount || isLegacyBusinessAccount) {
            this.redirectToStore();
        }

        return (
            <div className="checkoutWrap">
                <Helmet>
                    <title>{title}</title>
                </Helmet>
                {isBlockManagedAccountCheckout || shouldShowPreventCheckoutModalOnError ? (
                    <PreventSelfCheckoutModal defaultOpened handleClose={this.redirectToStore} />
                ) : (
                    <>
                        <section className="checkoutWrap__inside">
                            <LayoutComponentCheckoutHeader
                                naviTrigger={statusBarCheckoutTrigger}
                                naviLogo
                            />
                            <div className="checkoutWrap__content">
                                {order.id ? (
                                    <LayoutComponentAppear>
                                        {React.cloneElement(children, { checkoutAttemptReference })}
                                    </LayoutComponentAppear>
                                ) : null}
                            </div>
                            <LayoutComponentCheckoutFooter />
                        </section>
                        <NavigatorComponentCheckoutStatusBar />
                    </>
                )}
                <BusinessRedirectionModal />
            </div>
        );
    }
}

CheckoutWrap.contextTypes = {
    store: PropTypes.shape({}),
};

CheckoutWrap.propTypes = {
    order: PropTypes.shape({
        id: PropTypes.string,
        number: PropTypes.string,
    }).isRequired,
    orderFetching: PropTypes.bool.isRequired,
    translations: PropTypes.shape({}).isRequired,
    storeActive: PropTypes.shape({
        activeLanguage: PropTypes.string,
    }).isRequired,
    children: PropTypes.node.isRequired,
    authorize: PropTypes.func.isRequired,
    getOrder: PropTypes.func.isRequired,
    getAddressByUser: PropTypes.func.isRequired,
    getPaymentMethodsByUser: PropTypes.func.isRequired,
    getPaymentMethodsByStore: PropTypes.func.isRequired,
    statusBarCheckoutTrigger: PropTypes.func.isRequired,
    checkStoreAndUserType: PropTypes.func.isRequired,
    isBlockManagedAccountCheckout: PropTypes.bool.isRequired,
    shouldShowPreventCheckoutModalOnError: PropTypes.bool.isRequired,
};

const enhance = compose(
    connect(
        createStructuredSelector({
            order: orderSelector,
            orderFetching: orderFetchingSelector,
            translations: translationsSelector,
            storeActive: activeStoreSelector,
            isIncompleteBusinessAccount: isIncompleteBusinessAccountSelector,
            isLegacyBusinessAccount: isLegacyBusinessAccountSelector,
            isBlockManagedAccountCheckout: blockManagedAccountCheckoutEnabled,
            shouldShowPreventCheckoutModalOnError: shouldShowPreventCheckoutModalSelector,
        }),
        (dispatch) => ({
            authorize: bindActionCreators(authorizeAction, dispatch),
            getAddressByUser: bindActionCreators(getAddressByUserAction, dispatch),
            getPaymentMethodsByUser: bindActionCreators(getPaymentMethodsByUserAction, dispatch),
            getPaymentMethodsByStore: bindActionCreators(getPaymentMethodsByStoreAction, dispatch),
            statusBarCheckoutTrigger: bindActionCreators(statusBarCheckoutTriggerAction, dispatch),
            getOrder: bindActionCreators(getOrderAction, dispatch),
            checkStoreAndUserType: bindActionCreators(checkStoreAndUserTypeAction, dispatch),
            redirect: bindActionCreators(redirectAction, dispatch),
        }),
    ),
    withNethoneInitialization,
);

export default enhance(CheckoutWrap);
